import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Typography, Divider, Select, Upload, Tag, Tooltip, message } from "antd";
import { PlusOutlined, DeleteOutlined, InboxOutlined, EyeOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, InputItem, TextAreaItem, SelectItem } from "../../../../../Components/Items";
import { WA } from "../../theme";
import { CATEGORIAS, IDIOMAS, MODELOS, VARIABLES_SUGERIDAS, variablesDe, ejemploDe } from "../Helpers/plantillasHelpers";
import GuiaLlenadoPlantilla from "../GuiaLlenadoPlantilla";
import BarraFormato from "../BarraFormato";

const DROPDOWN = { zIndex: 1300 }; 

const { Text } = Typography;

const TIPOS_HEADER = [
  { value: "none", label: "Sin encabezado" },
  { value: "text", label: "Texto" },
  { value: "image", label: "Imagen" },
  { value: "document", label: "Documento (PDF)" },
];

const ID_CUERPO = "wa-plantilla-cuerpo";
const ID_HEADER = "wa-plantilla-header";

// Crea una plantilla nueva y la manda a aprobar a Meta (las plantillas existentes NO se editan).
const EditorPlantilla = ({ visible, headersToken, empresaId, onCancel, onGuardado }) => {
  const [nombre, setNombre] = useState("");
  const [idioma, setIdioma] = useState("es_MX");
  const [categoria, setCategoria] = useState("UTILITY");
  const [modelo, setModelo] = useState(undefined);
  const [headerTipo, setHeaderTipo] = useState("none");
  const [headerTexto, setHeaderTexto] = useState("");
  const [ejemplosHeader, setEjemplosHeader] = useState({});
  const [archivo, setArchivo] = useState(null);
  const [cuerpo, setCuerpo] = useState("");
  const [ejemplosCuerpo, setEjemplosCuerpo] = useState({});
  const [pie, setPie] = useState("");
  const [botones, setBotones] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [guiaVisible, setGuiaVisible] = useState(false);
  const campoActivo = useRef("cuerpo"); // "cuerpo" | "header"

  const varsCuerpo = variablesDe(cuerpo);
  const varsHeader = headerTipo === "text" ? variablesDe(headerTexto) : [];
  const sugeridas = (modelo && VARIABLES_SUGERIDAS[modelo]) || [];

  // Prellenar ejemplos automáticamente cuando aparece una variable nueva.
  useEffect(() => {
    setEjemplosCuerpo((prev) => {
      const next = { ...prev };
      let changed = false;
      varsCuerpo.forEach((n) => { if (next[n] === undefined) { next[n] = ejemploDe(n); changed = true; } });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuerpo]);
  useEffect(() => {
    setEjemplosHeader((prev) => {
      const next = { ...prev };
      let changed = false;
      varsHeader.forEach((n) => { if (next[n] === undefined) { next[n] = ejemploDe(n); changed = true; } });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerTexto, headerTipo]);

  const limpiar = () => {
    setNombre(""); setIdioma("es_MX"); setCategoria("UTILITY"); setModelo(undefined);
    setHeaderTipo("none"); setHeaderTexto(""); setEjemplosHeader({}); setArchivo(null);
    setCuerpo(""); setEjemplosCuerpo({}); setPie(""); setBotones([]);
  };
  const cerrar = () => { limpiar(); onCancel(); };

  useEffect(() => {
    if (!visible) limpiar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const sanitizarNombre = (v) =>
    v.toLowerCase().replace(/[^a-z0-9_]/g, "_").replace(/_+/g, "_").slice(0, 60);

  const insertarEn = (id, valor, setValor, fragmento) => {
    const el = document.getElementById(id);
    if (!el || typeof el.selectionStart !== "number") { setValor((valor || "") + fragmento); return; }
    const s = el.selectionStart, e = el.selectionEnd;
    const nuevo = valor.slice(0, s) + fragmento + valor.slice(e);
    setValor(nuevo);
    setTimeout(() => { el.focus(); const pos = s + fragmento.length; el.setSelectionRange(pos, pos); }, 0);
  };

  // Inserta una variable {{nombre}} en el campo activo (cuerpo o encabezado).
  const insertarVariable = (n) => {
    if (campoActivo.current === "header" && headerTipo === "text") {
      insertarEn(ID_HEADER, headerTexto, setHeaderTexto, `{{${n}}}`);
    } else {
      insertarEn(ID_CUERPO, cuerpo, setCuerpo, `{{${n}}}`);
    }
  };

  const agregarBoton = (tipo) =>
    setBotones((prev) => [...prev, tipo === "URL" ? { tipo: "URL", text: "", url: "", dinamica: false, ejemplo: "" } : { tipo: "QUICK_REPLY", text: "" }]);
  const setBoton = (i, campo, v) => setBotones((prev) => prev.map((b, idx) => (idx === i ? { ...b, [campo]: v } : b)));
  const quitarBoton = (i) => setBotones((prev) => prev.filter((_, idx) => idx !== i));

  const construirComponents = () => {
    const components = [];
    if (headerTipo === "text" && headerTexto.trim()) {
      const h = { type: "HEADER", format: "TEXT", text: headerTexto.trim() };
      if (varsHeader.length) {
        h.example = { header_text_named_params: varsHeader.map((n) => ({ param_name: n, example: ejemplosHeader[n] || "ejemplo" })) };
      }
      components.push(h);
    } else if (headerTipo === "image") {
      components.push({ type: "HEADER", format: "IMAGE" });
    } else if (headerTipo === "document") {
      components.push({ type: "HEADER", format: "DOCUMENT" });
    }
    const body = { type: "BODY", text: cuerpo.trim() };
    if (varsCuerpo.length) {
      body.example = { body_text_named_params: varsCuerpo.map((n) => ({ param_name: n, example: ejemplosCuerpo[n] || "ejemplo" })) };
    }
    components.push(body);
    if (pie.trim()) components.push({ type: "FOOTER", text: pie.trim() });
    if (botones.length) {
      components.push({
        type: "BUTTONS",
        buttons: botones.map((b) => {
          if (b.tipo !== "URL") return { type: "QUICK_REPLY", text: b.text };
          const comp = { type: "URL", text: b.text, url: b.url };
          if (b.dinamica) comp.example = [b.ejemplo || b.url.replace(/\{\{1\}\}/, "ejemplo")];
          return comp;
        }),
      });
    }
    return components;
  };

  const validar = () => {
    if (!nombre.trim()) return "Ponle un nombre a la plantilla.";
    if (!cuerpo.trim()) return "El cuerpo es obligatorio.";
    const cuerpoTrim = cuerpo.trim();
    if (/^\{\{[a-zA-Z0-9_]+\}\}/.test(cuerpoTrim)) return "El cuerpo no puede EMPEZAR con una variable; pon texto antes.";
    if (/\{\{[a-zA-Z0-9_]+\}\}$/.test(cuerpoTrim)) return "El cuerpo no puede TERMINAR con una variable; agrega texto después.";
    if (varsCuerpo.some((n) => !ejemplosCuerpo[n]?.trim())) return "Completa los ejemplos de todas las variables del cuerpo.";
    if (headerTipo === "text" && varsHeader.length > 1) return "El encabezado de texto admite máximo 1 variable.";
    if (headerTipo === "image" && !archivo) return "Sube una imagen de ejemplo para el encabezado.";
    if (headerTipo === "document" && !archivo) return "Sube un PDF de ejemplo para el encabezado.";
    if (botones.some((b) => !b.text.trim())) return "Cada botón necesita un texto.";
    if (botones.some((b) => b.tipo === "URL" && !/^https?:\/\//i.test(b.url || ""))) return "Las URL deben iniciar con http(s)://";
    if (botones.some((b) => b.tipo === "URL" && b.dinamica && !/\{\{1\}\}/.test(b.url || ""))) return "Una URL dinámica debe incluir {{1}} al final.";
    if (botones.some((b) => b.tipo === "URL" && b.dinamica && !b.ejemplo?.trim())) return "Pon una URL de ejemplo para la URL dinámica.";
    return null;
  };
  const errorValidacion = validar();

  const guardar = () => {
    const err = validar();
    if (err) return message.error(err);

    const fd = new FormData();
    fd.append("name", sanitizarNombre(nombre));
    fd.append("language", idioma);
    fd.append("category", categoria);
    if (modelo) fd.append("modelo", modelo);
    fd.append("components", JSON.stringify(construirComponents()));
    if ((headerTipo === "image" || headerTipo === "document") && archivo) fd.append("archivo", archivo);

    const headers = { ...headersToken };
    delete headers["Content-Type"];

    setGuardando(true);
    axios
      .post("whatsapp/plantillaCrear", fd, { headers })
      .then((r) => {
        if (r.data.success === 2) {
          message.success("Plantilla enviada a aprobación.");
          limpiar();
          onGuardado();
        } else {
          message.error(r.data.message || "No se pudo guardar la plantilla.");
        }
      })
      .catch(() => message.error("Error de red al guardar la plantilla."))
      .finally(() => setGuardando(false));
  };

  const tituloSeccion = { fontWeight: 600, margin: "12px 0 4px", color: WA.textPrimary, display: "flex", alignItems: "center", gap: 6 };
  const Etiqueta = ({ children, tip }) => (
    <div style={tituloSeccion}>
      <span>{children}</span>
      <Tooltip title={tip} zIndex={1300}>
        <QuestionCircleOutlined style={{ color: WA.textSecondary, fontSize: 13, cursor: "help" }} />
      </Tooltip>
    </div>
  );

  return (
    <ModalItem
      title="Nueva plantilla"
      visible={visible}
      onCancel={cerrar}
      width={820}
      zIndex={1200}
      destroyOnClose
      spinning={guardando}
    >
      <Etiqueta tip="Nombre interno (solo minúsculas, números y guion bajo). El cliente no lo ve.">Nombre (interno) *</Etiqueta>
      <InputItem
        placeholder="confirmacion_pedido"
        value={nombre}
        onChange={(e) => setNombre(sanitizarNombre(e.target.value))}
        maxLength={60}
      />

      <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
        <div style={{ flex: 1 }}>
          <Etiqueta tip="Idioma de la plantilla; debe coincidir con el del mensaje al enviarla.">Idioma</Etiqueta>
          <SelectItem value={idioma} onChange={setIdioma} width="100%" dropdownStyle={DROPDOWN}>
            {IDIOMAS.map((i) => <Select.Option key={i.value} value={i.value}>{i.label}</Select.Option>)}
          </SelectItem>
        </div>
        <div style={{ flex: 1 }}>
          <Etiqueta tip="Marketing = promociones; Utilidad = avisos transaccionales (pedidos, citas, etc.).">Categoría</Etiqueta>
          <SelectItem value={categoria} onChange={setCategoria} width="100%" dropdownStyle={DROPDOWN}>
            {CATEGORIAS.map((c) => <Select.Option key={c.value} value={c.value}>{c.label}</Select.Option>)}
          </SelectItem>
        </div>
      </div>

      <Etiqueta tip="Liga la plantilla a un módulo del sistema para autollenar variables al enviarla (opcional).">Modelo (opcional)</Etiqueta>
      <SelectItem value={modelo} onChange={setModelo} width="100%" allowClear dropdownStyle={DROPDOWN} placeholder="Ligar a un modelo para autollenar variables">
        {MODELOS.map((m) => <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>)}
      </SelectItem>

      <Divider style={{ margin: "16px 0 8px" }} />

      <Etiqueta tip="Parte superior del mensaje: texto, imagen o nada.">Encabezado</Etiqueta>
      <SelectItem value={headerTipo} onChange={setHeaderTipo} width="100%" dropdownStyle={DROPDOWN}>
        {TIPOS_HEADER.map((t) => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
      </SelectItem>
      {headerTipo === "text" && (
        <>
          <InputItem id={ID_HEADER} placeholder="Encabezado (admite 1 variable {{nombre}})" value={headerTexto}
            onFocus={() => { campoActivo.current = "header"; }}
            onChange={(e) => setHeaderTexto(e.target.value)} maxLength={60} style={{ marginTop: 6 }} />
          {varsHeader.map((n) => (
            <InputItem key={n} placeholder={`Ejemplo para {{${n}}}`} value={ejemplosHeader[n] || ""}
              onChange={(e) => setEjemplosHeader((p) => ({ ...p, [n]: e.target.value }))} style={{ marginTop: 6 }} />
          ))}
        </>
      )}
      {headerTipo === "image" && (
        <Upload.Dragger accept="image/*" showUploadList={false}
          beforeUpload={(f) => { setArchivo(f); return false; }} style={{ marginTop: 6 }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">{archivo ? `Seleccionado: ${archivo.name}` : "Sube una imagen de ejemplo"}</p>
        </Upload.Dragger>
      )}
      {headerTipo === "document" && (
        <>
          <Upload.Dragger accept=".pdf" showUploadList={false}
            beforeUpload={(f) => { setArchivo(f); return false; }} style={{ marginTop: 6 }}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{archivo ? `Seleccionado: ${archivo.name}` : "Sube un PDF de ejemplo"}</p>
          </Upload.Dragger>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Solo es un ejemplo para que Meta apruebe la plantilla. Al enviarla podrás adjuntar el PDF real del documento (cotización, pedido o venta).
          </Text>
        </>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, ...tituloSeccion }}>
        <span>Cuerpo *</span>
        <Tooltip title="Texto principal. Usa variables {{...}}; no las pongas al inicio ni al final." zIndex={1300}>
          <QuestionCircleOutlined style={{ color: WA.textSecondary, fontSize: 13, cursor: "help" }} />
        </Tooltip>
        <ButtonItem turquesa icon={<EyeOutlined />} onClick={() => setGuiaVisible(true)} style={{ marginLeft: "auto" }}>
          Ver ejemplo de llenado
        </ButtonItem>
      </div>

      <BarraFormato targetId={ID_CUERPO} value={cuerpo} onChange={setCuerpo} />
      <TextAreaItem
        id={ID_CUERPO}
        placeholder="Hola {{cliente}}, tu pedido {{serie_folio}} ya está listo."
        value={cuerpo}
        onFocus={() => { campoActivo.current = "cuerpo"; }}
        onChange={(e) => setCuerpo(e.target.value)}
        rows={3}
        maxLength={1024}
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        Una variable no puede ir al inicio ni al final del cuerpo (Meta lo rechaza).
      </Text>

      {sugeridas.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Variables sugeridas (clic para insertar en el campo activo):</Text>
          <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
            {sugeridas.map((n) => (
              <Tag key={n} color={WA.turquesa} style={{ cursor: "pointer" }} onClick={() => insertarVariable(n)}>
                {`{{${n}}}`}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {varsCuerpo.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Ejemplos de variables (se prellenan solos; ajústalos si quieres):</Text>
          </div>
          {varsCuerpo.map((n) => (
            <div key={n} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ ...etiquetaVar }}>{n}</span>
              <InputItem placeholder={`Ejemplo para {{${n}}}`} value={ejemplosCuerpo[n] || ""}
                onChange={(e) => setEjemplosCuerpo((p) => ({ ...p, [n]: e.target.value }))} />
            </div>
          ))}
        </div>
      )}

      <Etiqueta tip="Texto pequeño al final del mensaje (opcional).">Pie (opcional)</Etiqueta>
      <InputItem placeholder="Nota al pie" value={pie} onChange={(e) => setPie(e.target.value)} maxLength={60} />

      <Etiqueta tip="Botones de respuesta rápida o de enlace (URL). Máximo 3.">Botones (opcional)</Etiqueta>
      {botones.map((b, i) => (
        <div key={i} style={{ border: `1px solid ${WA.border}`, borderRadius: 8, padding: 8, marginBottom: 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, width: 70, color: WA.textSecondary }}>{b.tipo === "URL" ? "URL" : "Respuesta"}</span>
            <InputItem placeholder="Texto del botón" value={b.text} onChange={(e) => setBoton(i, "text", e.target.value)} maxLength={20} />
            {b.tipo === "URL" && (
              <SelectItem value={b.dinamica ? "din" : "est"} onChange={(v) => setBoton(i, "dinamica", v === "din")} width={130} dropdownStyle={DROPDOWN}>
                <Select.Option value="est">Estática</Select.Option>
                <Select.Option value="din">Dinámica</Select.Option>
              </SelectItem>
            )}
            <ButtonItem rojo icon={<DeleteOutlined />} onClick={() => quitarBoton(i)} />
          </div>
          {b.tipo === "URL" && (
            <div style={{ marginTop: 6 }}>
              <InputItem placeholder={b.dinamica ? "https://sitio.com/pedido/{{1}}" : "https://www.ejemplo.com"}
                value={b.url} onChange={(e) => setBoton(i, "url", e.target.value)} />
              {b.dinamica && (
                <>
                  <InputItem placeholder="URL de ejemplo (con un valor real)" value={b.ejemplo}
                    onChange={(e) => setBoton(i, "ejemplo", e.target.value)} style={{ marginTop: 4 }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>Pon {"{{1}}"} donde irá el dato variable (al final de la URL).</Text>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {botones.length < 3 && (
        <div style={{ display: "flex", gap: 8 }}>
          <ButtonItem lima icon={<PlusOutlined />} onClick={() => agregarBoton("QUICK_REPLY")}>Respuesta rápida</ButtonItem>
          <ButtonItem naranja2 icon={<PlusOutlined />} onClick={() => agregarBoton("URL")}>Botón URL</ButtonItem>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 20 }}>
        {errorValidacion && <Text type="danger" style={{ fontSize: 12, marginRight: "auto" }}>{errorValidacion}</Text>}
        <ButtonItem rojo onClick={cerrar}>Cancelar</ButtonItem>
        <ButtonItem cyan onClick={guardar} loading={guardando} disabled={!!errorValidacion}>Enviar a aprobación</ButtonItem>
      </div>

      <GuiaLlenadoPlantilla
        visible={guiaVisible}
        onClose={() => setGuiaVisible(false)}
        modelo={modelo}
        cuerpo={cuerpo}
        ejemplos={ejemplosCuerpo}
      />
    </ModalItem>
  );
};

const etiquetaVar = {
  fontSize: 12,
  fontWeight: 600,
  color: WA.turquesa,
  background: "rgba(0,191,214,0.12)",
  border: "1px solid rgba(0,191,214,0.35)",
  borderRadius: 6,
  padding: "2px 8px",
  minWidth: 110,
  textAlign: "center",
  whiteSpace: "nowrap",
};

export default EditorPlantilla;
